import type { PropsWithChildren } from 'react'
import { QuickSearchItem } from '@/scopes/search/components/QuickSearchBar/Item'
import { hasActiveItemSelector, useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { mqMin, space, tk, verticalGap } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { css, styled } from 'styled-components'

export const QuickSearchDrawer = styled.div`
  background-color: ${tk('color_semantic_bg')};
  border-radius: ${tk('radius_extra_large')};
  box-shadow: ${tk('shadow_md_bottom')};
  left: 0;
  min-height: 300px;
  overflow: hidden;
  padding: ${tk('spacing_md')};
  top: 0;
  width: 100%;
  z-index: 1;
`

const Wrapper = styled.div`
  padding-bottom:${space('primitives_space_48')};
  width: 100%;
  display: flex;
  justify-content: center;
  min-height: 80px;
`

const Drawer = styled(QuickSearchDrawer)<{ $hasActiveItem: boolean, $isDisabled?: boolean }>`
  width: calc(100% - 32px);
  max-width: 480px;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    max-width: 960px;
    padding: 8px 16px 8px 8px;
    min-height: 80px;
    border-radius: 40px;
    ${({ $hasActiveItem }) => $hasActiveItem && hasActiveItemMod()}
  }
  ${({ $isDisabled }) =>
    $isDisabled
    && css`
      pointer-events: none;
      background-color: ${tk('color_semantic_bg_disabled')};
    `}
`

function hasActiveItemMod() {
  return css`
    background-color: ${tk('color_semantic_bg_subdued_hover')};
  `
}

const InputItems = styled.div`
  ${verticalGap('spacing_xs')};
  @media ${tk('mq_desktop_md')} {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    margin-bottom: unset;

    ${QuickSearchItem}  {
        &:before {
          width: 0;
        }

        &:hover {
          background-color: #e7e0d7;
        }

        &:disabled:hover {
          background-color: ${tk('color_semantic_bg_disabled')};
        }

        &:first-of-type {
          padding-left: 20px;
          border-radius: 50px ${tk('radius_medium')} ${tk('radius_medium')} 50px;
        }

        &:last-of-type {
          margin-right: 8px;
          border-radius: ${tk('radius_medium')} 50px 50px ${tk('radius_medium')};
        }
      }

      > * {
        padding: 0 6px 0 12px;
        border-radius: 4px;
      }
    }

    > * {
      margin-bottom: unset;
      border-width: 0;
      border-radius: ${tk('radius_rounded')};
      position: relative;
      font-size: 14px;

      &:first-child {
        &:after {
          display: none;
        }
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        margin: auto 0;
        height: 40px;
        width: 1px;
        background: #ded2c4;
      }

      &:last-child {
        min-width: 175px;

        &:after {
          display: none;
        }
      }

  }
`

export function QuickSearchBar({
  disabled,
  children,
  className,
}: PropsWithChildren<{ disabled?: boolean, className?: string }>) {
  const hasActiveItem = useQuickSearchStore(hasActiveItemSelector)
  return (
    <Wrapper className={className}>
      <Drawer $hasActiveItem={hasActiveItem} $isDisabled={disabled}>
        <InputItems>
          {children}
        </InputItems>
      </Drawer>
    </Wrapper>
  )
}
export { PlaceholderText } from '@/scopes/search/components/QuickSearchBar/Item'
export { SelectionTextWrapper } from '@/scopes/search/components/QuickSearchBar/Item'
export { QuickSearchItem } from '@/scopes/search/components/QuickSearchBar/Item'
